@import "tailwindcss";
@import "tw-animate-css";

:root {
  --radius: 0.65rem;

  /* shadcn design tokens (light mode) - required by PromptBox */
  --background: 0 0% 100%;
  --foreground: 0 0% 14.5%;
  --card: 0 0% 100%;
  --card-foreground: 0 0% 14.5%;
  --popover: 0 0% 100%;
  --popover-foreground: 0 0% 14.5%;
  --primary: 0 0% 0%;
  --primary-foreground: 0 0% 98%;
  --secondary: 0 0% 96.1%;
  --secondary-foreground: 0 0% 9%;
  --muted: 0 0% 96.1%;
  --muted-foreground: 0 0% 45.1%;
  --accent: 0 0% 96.1%;
  --accent-foreground: 0 0% 9%;
  --destructive: 0 84.2% 60.2%;
  --destructive-foreground: 0 0% 98%;
  --border: 0 0% 89.8%;
  --input: 0 0% 89.8%;
  --ring: 0 0% 63.9%;

  /* MC brand palette */
  --mc-black: #000000;
  --mc-beige: #F0EFE0;
  --mc-white: #FFFFFF;
  --mc-charcoal: #252525;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: #F0EFE0;
  color: #252525;
}

#root {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

/* Widget mount target for the IIFE build */
#mc-sleep-advisor {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Custom scrollbar for the message list */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #d4d3c8 transparent;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #d4d3c8;
  border-radius: 2px;
}

/* shadcn token utilities used by PromptBox Radix components */
.bg-popover { background-color: hsl(var(--popover)); }
.text-popover-foreground { color: hsl(var(--popover-foreground)); }
.bg-accent { background-color: hsl(var(--accent)); }
.bg-card { background-color: hsl(var(--card)); }
.bg-background { background-color: hsl(var(--background)); }
.text-foreground { color: hsl(var(--foreground)); }
.text-muted-foreground { color: hsl(var(--muted-foreground)); }
.border-border { border-color: hsl(var(--border)); }
.ring-ring { --tw-ring-color: hsl(var(--ring)); }

/* ============================================================
   Doze / Claude-style aesthetic + cute animations
   ============================================================ */

.doze-serif {
  font-family: "Fraunces", "Georgia", serif;
  font-optical-sizing: auto;
}

/* Gentle idle float on the mascot */
@keyframes dozeFloat {
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50%      { transform: translateY(-3px) rotate(2deg); }
}
.doze-float {
  animation: dozeFloat 3.6s ease-in-out infinite;
  transform-origin: 50% 60%;
}

/* Floating "z z z" drifting up and fading */
.doze-zzz {
  position: absolute;
  top: -2px;
  right: -10px;
  width: 26px;
  height: 26px;
  pointer-events: none;
}
.doze-z {
  position: absolute;
  font-family: "Fraunces", Georgia, serif;
  font-weight: 600;
  color: #2B2A28;
  opacity: 0;
}
@keyframes dozeZ {
  0%   { opacity: 0; transform: translate(0, 2px) scale(0.7) rotate(-6deg); }
  25%  { opacity: 0.9; }
  100% { opacity: 0; transform: translate(9px, -16px) scale(1.15) rotate(8deg); }
}
.doze-z1 { font-size: 9px;  left: 0;  bottom: 4px; animation: dozeZ 2.8s ease-in-out infinite; }
.doze-z2 { font-size: 11px; left: 5px; bottom: 7px; animation: dozeZ 2.8s ease-in-out infinite 0.9s; }
.doze-z3 { font-size: 13px; left: 11px; bottom: 10px; animation: dozeZ 2.8s ease-in-out infinite 1.8s; }

/* Message entrance */
@keyframes dozeIn {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.doze-msg-in {
  animation: dozeIn 0.42s cubic-bezier(0.22, 1, 0.36, 1) both;
}

/* Cute typing dots while Doze is thinking */
.doze-typing { display: inline-flex; align-items: center; gap: 4px; }
.doze-typing span {
  width: 6px; height: 6px; border-radius: 9999px; background: #D2785A;
  display: inline-block;
  animation: dozeBounce 1.2s ease-in-out infinite;
}
.doze-typing span:nth-child(2) { animation-delay: 0.18s; }
.doze-typing span:nth-child(3) { animation-delay: 0.36s; }
@keyframes dozeBounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.45; }
  40%           { transform: translateY(-5px); opacity: 1; }
}

/* Soft entrance for the whole shell */
@keyframes dozeRise {
  from { opacity: 0; transform: translateY(14px) scale(0.99); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.doze-shell-in { animation: dozeRise 0.5s cubic-bezier(0.22, 1, 0.36, 1) both; }

@media (prefers-reduced-motion: reduce) {
  .doze-float, .doze-z, .doze-msg-in, .doze-typing span, .doze-shell-in {
    animation: none !important;
  }
  .doze-z { opacity: 0; }
}
